import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ToastProvider } from './contexts/ToastContext';
import { DialogProvider } from './contexts/DialogContext';
import { AuthProvider } from './contexts/AuthContext';
import { TransactionProvider } from './contexts/TransactionContext';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import AppLayout from './components/AppLayout';
import RootRedirect from './components/RootRedirect';
import NotFound from './components/NotFound';

// 공개 페이지 컴포넌트
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import OAuthCallback from './components/OAuthCallback';

// 보호 페이지 컴포넌트
import Dashboard from './components/Dashboard';
import CalendarView from './components/CalendarView';
import TransactionForm from './components/TransactionForm';
import TransactionList from './components/TransactionList';
import DataExport from './components/DataExport';
import Analytics from './components/Analytics';
import Settings from './components/Settings';

import './App.css';

/**
 * App — 앱 진입점
 *
 * BrowserRouter > ToastProvider > DialogProvider > AuthProvider > TransactionProvider > Routes 구조로 구성.
 * - BrowserRouter가 최상위에 위치하여 Context 내부에서 useNavigate 등 Router 훅 사용 가능
 * - ToastProvider와 DialogProvider가 AuthProvider 밖에 위치하여 Context 내부에서 useToast(), useDialog() 사용 가능
 * - 모든 상태 관리는 Context에 위임, App.js는 라우팅 설정만 담당
 * - 공개 라우트: login, register, forgot-password, oauth/callback
 * - 보호 라우트: ProtectedRoute > AppLayout > Outlet 구조
 *
 * 웹 전략: 조회/분석에 집중, 입력/AI는 모바일 전용
 * - 제거된 라우트: /goals, /ai, /ai-chat, /notifications
 * - 추가된 라우트: /recurring (고정 거래 관리), /export (데이터 내보내기)
 * - /transactions/add는 유지 (직접 URL 접근 + FAB 모달 지원)
 */
function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <DialogProvider>
          <AuthProvider>
            <TransactionProvider>
              <Routes>
                {/* 공개 라우트 */}
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/oauth/callback" element={<OAuthCallback />} />

                {/* 보호 라우트 — AppLayout이 사이드바 + Outlet 렌더링 */}
                <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/calendar" element={<CalendarView />} />
                  <Route path="/transactions/add" element={<TransactionForm />} />
                  <Route path="/transactions" element={<TransactionList />} />
                  <Route path="/export" element={<DataExport />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>

                {/* 루트 리다이렉트 */}
                <Route path="/" element={<RootRedirect />} />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </TransactionProvider>
          </AuthProvider>
        </DialogProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
